import { LightBulbIcon, PlusIcon } from 'assets/icons';
import { Album } from 'models/album';
import { AlbumThumbnail } from 'common/album-thumbnail';
import { getAlbumRepresentativeIcon, classnames } from 'helpers/utils';
import styles from './interview-list-item.module.scss';

export type InterviewListItemType =
  | 'customTopic'
  | 'album'
  | 'unanswered'
  | 'uploading';

interface InterviewListItemProps {
  type: InterviewListItemType;
  title: string;
  album?: Album;
  isSelected?: boolean;
  recordedInThisSession?: boolean;
  uploadProgress?: number;
  onClick?: () => void;
}

const Thumbnail = ({
  album,
  type,
}: {
  album?: Album;
  type: InterviewListItemType;
}) => {
  if (type === 'album' || type === 'uploading') {
    if (!album) {
      return <div className={styles.thumbnailFallback} />;
    }
    return (
      <AlbumThumbnail
        album={album}
        className={styles.thumbnailImage}
        fallback={
          <div className={styles.thumbnailFallback}>
            {getAlbumRepresentativeIcon(album)}
          </div>
        }
      />
    );
  }

  if (type === 'unanswered') {
    return (
      <div className={styles.unansweredIcon}>
        <LightBulbIcon />
      </div>
    );
  }

  return (
    <div className={styles.customTopicIcon}>
      <PlusIcon />
    </div>
  );
};

const ProgressCircle = ({ progress = 0 }: { progress?: number }) => (
  <div className={styles.progressContainer}>
    <svg className={styles.progressCircle} viewBox="0 0 20 20">
      <circle
        className={styles.progressBackground}
        cx="10"
        cy="10"
        r="7"
        fill="none"
        strokeWidth="2"
      />
      <circle
        className={styles.progressFill}
        cx="10"
        cy="10"
        r="7"
        fill="none"
        strokeWidth="2"
        strokeDasharray={`${progress * 0.44} 44`}
        strokeLinecap="round"
        transform="rotate(-90 10 10)"
      />
    </svg>
  </div>
);

export const InterviewListItem = ({
  type,
  title,
  album,
  isSelected = false,
  recordedInThisSession = false,
  uploadProgress,
  onClick,
}: InterviewListItemProps) => {
  const containerClasses = classnames(styles.container, {
    [styles.selected]: isSelected,
    [styles.customTopic]: type === 'customTopic',
    [styles.album]: type === 'album',
    [styles.unanswered]: type === 'unanswered',
    [styles.uploading]: type === 'uploading',
  });

  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      className={containerClasses}
      onClick={onClick}
    >
      <div className={styles.itemWrapper}>
        <div className={styles.thumbnailWrapper}>
          <Thumbnail album={album} type={type} />
        </div>

        <div className={styles.contentWrapper}>
          <span className={styles.title}>{title}</span>

          {type === 'uploading' && uploadProgress !== undefined && (
            <ProgressCircle progress={uploadProgress} />
          )}
        </div>

        {recordedInThisSession && (
          <div className={styles.badge}>
            <span>Recorded now</span>
          </div>
        )}
      </div>
    </Component>
  );
};
